import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faXmark,
  faVanShuttle,
  faTaxi,
  faCarSide,
  faUserGroup,
  faCircleCheck,
  faBoltLightning,
  faArrowUpRightFromSquare,
  faUser
} from '@fortawesome/free-solid-svg-icons'
import { urlApi } from '../../config'
import { getAccessToken } from '../../auth'
import { progress, passengerCount, localityName, formatTime, formatMoney } from './tripUtils'

const vehicle = type => {
  if (type === 'van') return { icon: faVanShuttle, label: 'Van' }
  if (type === 'taxi') return { icon: faTaxi, label: 'Taxi' }
  return { icon: faCarSide, label: 'Carpool' }
}

const Face = ({ src, name, size, ring }) => (
  <div className='p-[2px] rounded-full shrink-0' style={{ background: ring }}>
    <Avatar
      src={src || undefined}
      sx={{ width: size, height: size, fontSize: size * 0.42, fontWeight: 700, background: 'var(--panel-soft)', color: 'var(--text-soft)', border: '2px solid var(--panel)' }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </Avatar>
  </div>
)

const TripDrawer = ({ tripId, fallback, now, onClose }) => {
  const navigate = useNavigate()
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    ;(async () => {
      try {
        const token = await getAccessToken()
        const res = await fetch(`${urlApi}/admin/lobbies/${tripId}`, {
          credentials: 'include',
          headers: { ...(typeof token === 'string' && { Authorization: `Bearer ${token}` }) }
        })
        if (res.ok && alive) setDetail(await res.json())
      } catch (e) {
        /* mantiene fallback */
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [tripId])

  useEffect(() => {
    const onKey = e => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const trip = detail || fallback || {}
  const v = vehicle(trip.type)
  const pct = Math.round(progress(trip, now) * 100)
  const companions = Array.isArray(trip.companions) ? trip.companions : []
  const pax = passengerCount(trip)
  const driver = trip.Owner_info || {}
  const driverId = driver.id || driver.user_id
  const money = formatMoney(trip.price, trip.currency)

  const openInPanel = () => { onClose(); navigate(`/lobbies?lobby_id=${encodeURIComponent(trip._id || tripId)}`) }
  const openUser = () => { if (driverId) { onClose(); navigate(`/user-search?user_id=${encodeURIComponent(driverId)}`) } }

  return (
    <div className='live-overlay fixed inset-0 z-[1000] flex justify-end' style={{ background: 'rgba(20,16,12,0.45)' }} onClick={onClose}>
      <div
        className='live-drawer live-scroll relative w-full max-w-[440px] h-full overflow-y-auto'
        style={{ background: 'var(--panel)', color: 'var(--text)', boxShadow: 'var(--shadow-pop)', borderLeft: '1px solid var(--border)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className='sticky top-0 z-10 flex items-center gap-3 px-5 py-4 border-b' style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <span className='flex items-center justify-center w-9 h-9 rounded-xl' style={{ background: 'var(--coral-soft)', color: 'var(--coral)' }}>
            <FontAwesomeIcon icon={v.icon} />
          </span>
          <div className='min-w-0'>
            <div className='text-[15px] font-bold leading-tight truncate'>{localityName(trip?.Journey?.origin_name)} → {localityName(trip?.Journey?.destination_name)}</div>
            <div className='text-xs' style={{ color: 'var(--text-soft)' }}>{v.label}{pct > 0 && pct < 100 ? ` · en curso ${pct}%` : ''}</div>
          </div>
          <button onClick={onClose} className='ml-auto w-9 h-9 flex items-center justify-center rounded-xl shrink-0' style={{ background: 'var(--panel-soft)', color: 'var(--text-soft)', border: '1px solid var(--border)' }}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className='p-5'>
          {/* Precio */}
          {money && (
            <div className='flex items-baseline gap-2 mb-4'>
              <span className='text-3xl font-black tracking-tight'>{money}</span>
              <span className='text-xs' style={{ color: 'var(--text-faint)' }}>por asiento</span>
            </div>
          )}

          {/* Línea de tiempo de la ruta */}
          <div className='rounded-2xl border p-4' style={{ borderColor: 'var(--border)', background: 'var(--panel-soft)' }}>
            <div className='flex gap-3'>
              <div className='flex flex-col items-center pt-1'>
                <span className='w-3 h-3 rounded-full' style={{ background: 'var(--coral)' }} />
                <span className='flex-1 w-0.5 my-1' style={{ background: 'var(--border-strong)' }} />
                <span className='w-3 h-3 rounded-full border-2' style={{ borderColor: 'var(--coral)' }} />
              </div>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center justify-between'>
                  <span className='font-semibold truncate'>{localityName(trip?.Journey?.origin_name)}</span>
                  <span className='tabular-nums text-sm' style={{ color: 'var(--text-soft)' }}>{formatTime(trip?.Journey?.departure_time)}</span>
                </div>
                <div className='flex items-center justify-between mt-5'>
                  <span className='font-semibold truncate'>{localityName(trip?.Journey?.destination_name)}</span>
                  <span className='tabular-nums text-sm' style={{ color: 'var(--text-soft)' }}>{formatTime(trip?.Journey?.estimated_time)}</span>
                </div>
              </div>
            </div>
            {pct > 0 && pct < 100 && (
              <div className='mt-3 h-1.5 rounded-full overflow-hidden' style={{ background: 'var(--track)' }}>
                <div className='h-full rounded-full' style={{ width: `${pct}%`, background: 'linear-gradient(90deg,var(--amber),var(--coral))' }} />
              </div>
            )}
          </div>

          {/* Conductor */}
          <button onClick={openUser} className='w-full mt-4 flex items-center gap-3 text-left rounded-2xl border p-3 transition-colors' style={{ borderColor: 'var(--border)' }}>
            <Face src={driver.picture} name={driver.first_name} size={48} ring='linear-gradient(135deg,var(--amber),var(--coral))' />
            <div className='min-w-0 flex-1'>
              <div className='font-semibold flex items-center gap-1.5'>
                {driver.first_name || 'Conductor'}
                {driver.verified && <FontAwesomeIcon icon={faCircleCheck} style={{ color: 'var(--green)' }} className='text-xs' />}
              </div>
              <div className='text-xs' style={{ color: 'var(--text-soft)' }}>Conductor · ver perfil</div>
            </div>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ color: 'var(--text-faint)' }} className='text-xs' />
          </button>

          {/* Pasajeros */}
          <div className='mt-4'>
            <div className='flex items-center justify-between mb-2'>
              <span className='text-[11px] uppercase tracking-wider' style={{ color: 'var(--text-faint)' }}>Pasajeros</span>
              <span className='flex items-center gap-1.5 font-bold text-sm' style={{ color: 'var(--green)' }}>
                <FontAwesomeIcon icon={faUserGroup} className='text-[11px]' /> {pax}
              </span>
            </div>
            {loading && !detail ? (
              <div className='text-sm py-2' style={{ color: 'var(--text-faint)' }}>Cargando…</div>
            ) : companions.length === 0 ? (
              <div className='text-sm py-2' style={{ color: 'var(--text-faint)' }}>Sin pasajeros.</div>
            ) : (
              <div className='flex flex-col gap-1.5'>
                {companions.map((c, i) => (
                  <div key={c.user_id || i} className='flex items-center gap-3 rounded-xl px-2 py-1.5' style={{ background: 'var(--panel-soft)' }}>
                    <Face src={c.picture} name={c.first_name} size={34} ring='linear-gradient(135deg,var(--coral),var(--coral-deep))' />
                    <span className='text-sm font-medium truncate flex-1'>{c.first_name || 'Pasajero'}</span>
                    <span className='text-xs' style={{ color: 'var(--text-faint)' }}>{(c.seats || 1)} asiento{(c.seats || 1) > 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Acciones: abrir en el panel normal */}
          <div className='mt-6 grid grid-cols-1 gap-2'>
            <button onClick={openInPanel} className='flex items-center justify-center gap-2 h-11 rounded-xl font-semibold text-white transition-transform hover:-translate-y-0.5' style={{ background: 'var(--coral)' }}>
              <FontAwesomeIcon icon={faBoltLightning} className='text-sm' /> Ver viaje en el panel
            </button>
            <button onClick={openUser} disabled={!driverId} className='flex items-center justify-center gap-2 h-11 rounded-xl font-semibold transition-colors disabled:opacity-40' style={{ background: 'var(--panel-soft)', color: 'var(--text)', border: '1px solid var(--border)' }}>
              <FontAwesomeIcon icon={faUser} className='text-sm' /> Ver usuario
            </button>
          </div>

          <div className='mt-4 text-[11px]' style={{ color: 'var(--text-faint)' }}>ID: {trip._id || tripId}</div>
        </div>
      </div>
    </div>
  )
}

export default TripDrawer
